<template>
 <div id="app-wrapper">
  <div class="fixed-background"></div>
  <div class="content-scroll-wrapper">
   <ToastContainer /> 
   <MainLayout :app-state="appState">
    <template #header><AppHeader /></template>
    
    <template #main>
        <Transition name="slide-fade" mode="out-in">
        <div v-if="appState === 'LOADING'" class="status-container" key="loading">
            <LoadingSpinner />
            <p>Mencari data siswa...</p> <!-- Pesan loading yang lebih spesifik -->
        </div>

        <ResultSearch 
            v-else-if="appState === 'SEARCH_FORM'"
            :is-searching="isSearching"
            :found-student="foundStudent"
            :error="initialError"  
            @search-by-name="handleStudentSearchByName"
            @clear-error="initialError = null; foundStudent = null"
            @view-result="showStudentResult"
        />

        <ResultsStudent v-else-if="appState === 'RESULT'" :results="finalRecommendations" />
        
        <!-- [PERUBAHAN] Tombol di halaman error sekarang memanggil resetToSearch -->
        <div v-else-if="appState === 'ERROR'" class="status-container error" key="error">
            <h2>Oops! Terjadi Kesalahan</h2>
            <p>{{ initialError }}</p>
            <button @click="resetToSearch" class="retry-button">Cari Lagi</button>
        </div>
        </Transition>
    </template>
    <template #footer><AppFooter /></template>
   </MainLayout>
  </div>
 </div>
</template>

<script setup>
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import MainLayout from '@/layouts/MainLayout.vue';
import ResultSearch from '@/components/ResultSearch.vue';
import ResultsStudent from '@/components/ResultsStudent.vue';
import { ref, onMounted, onBeforeUnmount } from 'vue';

// --- State Induk ---
const appState = ref('SEARCH_FORM'); // [PERUBAHAN] Langsung ke form pencarian
const initialError = ref(null);
const finalRecommendations = ref(null);
const isSearching = ref(false);
const foundStudent = ref(null);

// ---- Back button / browser navigation guard ----
function handleBeforeUnload(e) {
  if (appState.value === 'SEARCH_FORM') return;
  e.preventDefault();
  e.returnValue = '';
}

function handlePopState() {
  if (appState.value === 'SEARCH_FORM') return;
  history.pushState(null, '', window.location.href);
  const confirmed = window.confirm('Apakah kamu yakin ingin keluar?');
  if (confirmed) {
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    history.back();
  }
}

onMounted(() => {
  history.pushState(null, '', window.location.href);
  window.addEventListener('popstate', handlePopState);
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handlePopState);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

async function handleStudentSearchByName(studentName) {
    isSearching.value = true;
    initialError.value = null;
    foundStudent.value = null;

    try {
    const nameApiUrl = `${process.env.VUE_APP_API_URL}/getstudent/${encodeURIComponent(studentName)}`;
    const nameResponse = await fetch(nameApiUrl);
    const nameData = await nameResponse.json();

    if (!nameResponse.ok || !nameData.data.id) {
        initialError.value = 'Data kamu tidak ditemukan! <br>Nama harus sesuai dengan yang terdaftar di sistem.';
    } else {
        // Sukses: simpan data siswa yang ditemukan
        foundStudent.value = { id: nameData.data.id, name: studentName };
    }
    } catch (err) {
        initialError.value = 'Gagal terhubung ke server.';
        console.error("API call error:", err);
    } finally {
        isSearching.value = false;
    }
}

async function showStudentResult(studentId) {
    appState.value = 'LOADING'; 
    finalRecommendations.value = null;
    try {
        const resultApiUrl = `${process.env.VUE_APP_API_URL}/getstudentresult/${studentId}`;
        const response = await fetch(resultApiUrl);
        if (!response.ok) throw new Error('Gagal mengambil hasil tes.');
        
        const resultData = await response.json();
        finalRecommendations.value = resultData.data;
        appState.value = 'RESULT';
    } catch (err) {
        initialError.value = err.message;
        appState.value = 'ERROR';
    }
}

// [BARU] Fungsi untuk mereset ke halaman pencarian dari halaman error
function resetToSearch() {
    initialError.value = null;
    appState.value = 'SEARCH_FORM';
}

</script>

<style scoped>
/* Tambahkan gaya untuk halaman hasil yang baru */
.result-display-container {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    margin-top: 5%;
}
.result-card {
    width: 100%;
    max-width: 600px;
    background: white;
    padding: 2.5rem;
    border-radius: 24px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
    text-align: center;
}
.result-card h3 {
    color: #555;
    font-weight: 400;
    margin: 0;
}
.result-card h1 {
    font-family: 'Epilogue', sans-serif;
    font-weight: 900;
    font-size: 2.5rem;
    color: #2c3e50;
    margin: 0.5rem 0 2rem 0;
}
.result-details {
    text-align: left;
    margin-bottom: 2.5rem;
    border-top: 1px solid #eee;
    padding-top: 1.5rem;
}
.result-details p {
    font-size: 1.1rem;
    margin: 0.8rem 0;
}
.score {
    font-weight: bold;
    font-size: 1.2rem;
    color: #FF6B6B;
}
.search-again-button {
    background-color: #3498db;
    color: white;
    border: none;
    padding: 0.8rem 1.5rem;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.3s ease;
}
.search-again-button:hover {
    background-color: #2980b9;
}
.status-container {
    text-align: center;
    margin-top: 20%;
}
</style>